import React, { useEffect, useState } from 'react';

interface SendEmailDialogProps {
  open: boolean;
  email: {
    id: number;
    subject: string;
  } | null;
  onClose: () => void;
  onSend: (emailId: number, sportId: number) => void;
  onQuickSend: (subject: string, body: string, sportId: number | null) => void;
  apiBaseUrl: string;
  accessToken?: string;
  userRoles?: string[];
}

function hasRole(roles: string[] | undefined, role: string): boolean {
  if (!roles || !Array.isArray(roles)) return false;
  return roles.map(r => r.toLowerCase()).includes(role.toLowerCase());
}

function getUserSports(roles: string[] | undefined): string[] {
  if (!roles || !Array.isArray(roles)) return [];
  return roles
    .filter(r => typeof r === 'string' && r.startsWith('sports_admin_'))
    .map(r => r.replace('sports_admin_', '').toLowerCase());
}

const SendEmailDialog: React.FC<SendEmailDialogProps> = (props) => {
  const { open, email, onClose, onSend, onQuickSend, apiBaseUrl, accessToken, userRoles } = props;
  const [sports, setSports] = useState<{ id: number; name: string }[]>([]);
  const [selectedSportId, setSelectedSportId] = useState<number | null>(null);
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [visibleMemberCount, setVisibleMemberCount] = useState<number | null>(null);

  // Quick send state
  const [quickSubject, setQuickSubject] = useState('');
  const [quickBody, setQuickBody] = useState('');
  const [quickLoading, setQuickLoading] = useState(false);

  const isAdmin = hasRole(userRoles, 'admin');
  const isTrustee = hasRole(userRoles, 'trustee');
  const isSportsAdmin = getUserSports(userRoles).length > 0;
  const canSend = isAdmin || isTrustee || isSportsAdmin;

  useEffect(() => {
    if (open) {
      fetch(`${apiBaseUrl}/sports`, {
        headers: { 'Content-Type': 'application/json', ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}) },
      })
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) setSports(data);
        });
      setSelectedSportId(null);
      setMemberCount(null);
      setVisibleMemberCount(null);
      setQuickSubject('');
      setQuickBody('');
    }
  }, [open, apiBaseUrl, accessToken]);

  // Get visible member count (admins/trustees see all, sports admins see their sport's members)
  useEffect(() => {
    if (!open) return;
    fetch(`${apiBaseUrl}/email/visible-members/count`, {
      headers: { 'Content-Type': 'application/json', ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}) },
    })
      .then(res => res.json())
      .then(data => {
        if (typeof data.count === 'number') setVisibleMemberCount(data.count);
      })
      .catch(() => {});
  }, [open, apiBaseUrl, accessToken]);

  useEffect(() => {
    if (selectedSportId) {
      fetch(`${apiBaseUrl}/sports/${selectedSportId}/members/count`, {
        headers: { 'Content-Type': 'application/json', ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}) },
      })
        .then(res => res.json())
        .then(data => {
          if (typeof data.count === 'number') setMemberCount(data.count);
        });
    } else {
      setMemberCount(null);
    }
  }, [selectedSportId, apiBaseUrl, accessToken]);

  const handleQuickSend = async () => {
    if (!quickSubject || !quickBody) return;
    setQuickLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/email/send-direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          subject: quickSubject,
          body: quickBody,
          sportId: selectedSportId,
        }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        alert(`Email sent to ${data.sent} member${data.sent !== 1 ? 's' : ''}.${data.failed > 0 ? ` ${data.failed} failed.` : ''}`);
        setQuickSubject('');
        setQuickBody('');
        setSelectedSportId(null);
        onClose();
      } else {
        alert('Failed to send email: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Network error while sending email.');
    } finally {
      setQuickLoading(false);
    }
  };

  // Determine what the user can send to
  // For admins/trustees: show "All visible members" + individual sports
  // For sports admins: show their sports only (no "all" option, they can only see their sport members)
  const showAllOption = (isAdmin || isTrustee) && visibleMemberCount !== null && visibleMemberCount > 0;

  // Sports admins can send to any of their sports
  const userSports = getUserSports(userRoles);
  const sportsForUser = (isAdmin || isTrustee) ? sports : sports.filter(s => userSports.includes(s.name.toLowerCase()));

  if (!open) return null;

  // email === null → quick send form; email has content → draft send form
  const isQuickSend = email === null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">Send Email</h2>

        {isQuickSend ? (
          /* Quick Send form */
          <div className="space-y-4">
            <div>
              <label className="block font-semibold mb-1">Subject</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded"
                value={quickSubject}
                onChange={e => setQuickSubject(e.target.value)}
                placeholder="Enter subject..."
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">Body</label>
              <textarea
                className="w-full px-3 py-2 border rounded"
                rows={6}
                value={quickBody}
                onChange={e => setQuickBody(e.target.value)}
                placeholder="Enter email body..."
              />
            </div>
            <div>
              <label className="block font-semibold mb-1">Send to:</label>
              {showAllOption && (
                <label className="flex items-center gap-2 mb-1">
                  <input
                    type="radio"
                    checked={selectedSportId === null}
                    onChange={() => setSelectedSportId(null)}
                  />
                  <span className="font-medium">All visible members ({visibleMemberCount})</span>
                </label>
              )}
              {sportsForUser.map(sport => (
                <label key={sport.id} className="flex items-center gap-2 mb-1">
                  <input
                    type="radio"
                    checked={selectedSportId === sport.id}
                    onChange={() => setSelectedSportId(sport.id)}
                  />
                  <span>{sport.name}</span>
                </label>
              ))}
              {!showAllOption && sportsForUser.length === 0 && (
                <div className="text-gray-500 italic">No sports available to send to.</div>
              )}
            </div>
            <div className="text-sm text-gray-600">
              {selectedSportId
                ? `${memberCount || 0} member${(memberCount || 0) !== 1 ? 's' : ''} will receive this email.`
                : showAllOption
                  ? `${visibleMemberCount} member${visibleMemberCount !== 1 ? 's' : ''} will receive this email.`
                  : 'Select a destination above.'}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                onClick={handleQuickSend}
                disabled={quickLoading || (!quickSubject || !quickBody)}
              >
                {quickLoading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        ) : (
          /* Draft send form */
          email && (
            <>
              <div className="mb-4">
                <div className="font-semibold">Subject:</div>
                <div>{email.subject}</div>
              </div>
              <div className="mb-4">
                <label className="font-semibold block mb-2">Select Sport:</label>
                <select
                  className="w-full px-3 py-2 border rounded"
                  value={selectedSportId ?? ''}
                  onChange={e => setSelectedSportId(Number(e.target.value) || null)}
                >
                  <option value="">Select a sport...</option>
                  {sports.map(sport => (
                    <option key={sport.id} value={sport.id}>{sport.name}</option>
                  ))}
                </select>
              </div>
              {selectedSportId && memberCount !== null && (
                <div className="mb-4 text-blue-700 font-semibold">
                  {memberCount} member{memberCount !== 1 ? 's' : ''} match the selection.
                </div>
              )}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  className="bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded"
                  onClick={() => selectedSportId && onSend(email.id, selectedSportId)}
                  disabled={!selectedSportId}
                >
                  Send
                </button>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
};

export default SendEmailDialog;
